import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

// Mapeo de rutas a secciones de ayuda
const routeToHelpSection: Record<string, string> = {
  '/login': 'login',
  '/setup': 'setup',
  '/events': 'events',
  '/live-tail': 'liveTail',
  '/signals': 'signals',
  '/dashboards': 'dashboards',
  '/alerts': 'alerts',
  '/anomalies': 'anomalies',
  '/settings': 'settings',
  '/users': 'users',
  '/audit': 'audit',
  '/metrics': 'metrics',
  '/traces': 'traces',
  '/health-checks': 'healthChecks',
  '/services': 'services',
  '/postmortems': 'postmortems',
}

// Secciones de ayuda disponibles (los títulos se traducen en el componente)
export const helpSections = [
  { id: 'overview', titleKey: 'help.sections.overview', icon: 'Home' },
  { id: 'login', titleKey: 'help.sections.login', icon: 'LogIn' },
  { id: 'setup', titleKey: 'help.sections.setup', icon: 'Shield' },
  { id: 'navigation', titleKey: 'help.sections.navigation', icon: 'Layout' },
  { id: 'events', titleKey: 'help.sections.events', icon: 'Search' },
  { id: 'liveTail', titleKey: 'help.sections.liveTail', icon: 'Activity' },
  { id: 'signals', titleKey: 'help.sections.signals', icon: 'Filter' },
  { id: 'dashboards', titleKey: 'help.sections.dashboards', icon: 'LayoutDashboard' },
  { id: 'alerts', titleKey: 'help.sections.alerts', icon: 'Bell' },
  { id: 'anomalies', titleKey: 'help.sections.anomalies', icon: 'AlertTriangle' },
  { id: 'settings', titleKey: 'help.sections.settings', icon: 'Settings' },
  { id: 'users', titleKey: 'help.sections.users', icon: 'Users' },
  { id: 'audit', titleKey: 'help.sections.audit', icon: 'FileText' },
  { id: 'metrics', titleKey: 'help.sections.metrics', icon: 'Gauge' },
  { id: 'traces', titleKey: 'help.sections.traces', icon: 'GitBranch' },
  { id: 'healthChecks', titleKey: 'help.sections.healthChecks', icon: 'HeartPulse' },
  { id: 'services', titleKey: 'help.sections.services', icon: 'Network' },
  { id: 'postmortems', titleKey: 'help.sections.postmortems', icon: 'FileText' },
  { id: 'mcp', titleKey: 'help.sections.mcp', icon: 'Bot' },
] as const

export type HelpSectionId = typeof helpSections[number]['id']

interface HelpContextType {
  isOpen: boolean
  currentSection: HelpSectionId
  openHelp: (section?: HelpSectionId) => void
  closeHelp: () => void
  navigateToSection: (section: HelpSectionId) => void
  openContextualHelp: () => void
}

const HelpContext = createContext<HelpContextType | undefined>(undefined)

export function HelpProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [currentSection, setCurrentSection] = useState<HelpSectionId>('overview')
  const location = useLocation()

  const openHelp = useCallback((section?: HelpSectionId) => {
    if (section) {
      setCurrentSection(section)
    }
    setIsOpen(true)
  }, [])

  const closeHelp = useCallback(() => {
    setIsOpen(false)
  }, [])

  const navigateToSection = useCallback((section: HelpSectionId) => {
    setCurrentSection(section)
  }, [])

  // Abre la ayuda en la sección correspondiente a la ruta actual
  const openContextualHelp = useCallback(() => {
    const path = location.pathname

    // Buscar coincidencia exacta primero
    let section = routeToHelpSection[path]

    // Si no hay coincidencia exacta, buscar por prefijo (para rutas como /dashboards/123)
    if (!section) {
      for (const [route, helpSection] of Object.entries(routeToHelpSection)) {
        if (path.startsWith(route)) {
          section = helpSection
          break
        }
      }
    }

    setCurrentSection((section as HelpSectionId) || 'overview')
    setIsOpen(true)
  }, [location.pathname])

  const value = useMemo(() => ({
    isOpen,
    currentSection,
    openHelp,
    closeHelp,
    navigateToSection,
    openContextualHelp,
  }), [isOpen, currentSection, openHelp, closeHelp, navigateToSection, openContextualHelp])

  return (
    <HelpContext.Provider value={value}>
      {children}
    </HelpContext.Provider>
  )
}

export function useHelp() {
  const context = useContext(HelpContext)
  if (!context) {
    throw new Error('useHelp must be used within a HelpProvider')
  }
  return context
}
